/**
 * Indexes précalculés pour accès O(1) aux entités de la BDD.
 *
 * Les loaders renvoient les fichiers JSON bruts ; ce module construit, après
 * un chargement unique, les maps et tableaux dérivés utilisés par toutes les
 * pages de l'app. Chaque index est calculé à la volée puis mémoïsé pour la
 * durée de la session.
 */

import { parseNc } from '@domain/creatures';
import { type BDD, loadBDD } from './loaders';
import type {
  Creature,
  CreatureModificateur,
  Peuple,
  Profil,
  VoieClasse,
  VoiePeuple,
  VoiePrestige,
} from './schemas';

export type CreatureCategorie = 'humanoides' | 'animaux' | 'fantastiques';

export interface BDDIndexes {
  readonly bdd: BDD;

  // Profils et voies
  readonly profilById: ReadonlyMap<string, Profil>;
  readonly voieClasseById: ReadonlyMap<string, VoieClasse>;
  readonly voiesClasseByProfil: ReadonlyMap<string, readonly VoieClasse[]>;
  /**
   * La Voie du Mage est accessible aux 4 profils mage mais n'est plus
   * comptée dans `voies_classe_ids` des profils (décision BDD). Elle est
   * exposée ici pour les pages de détail et le wizard.
   */
  readonly voieDuMage: VoieClasse | null;
  readonly voiePrestigeById: ReadonlyMap<string, VoiePrestige>;
  readonly voiesPrestigeByCategorie: ReadonlyMap<string, readonly VoiePrestige[]>;

  // Peuples
  readonly peupleById: ReadonlyMap<string, Peuple>;
  readonly voiePeupleById: ReadonlyMap<string, VoiePeuple>;

  // Bestiaire
  readonly creaturesAll: readonly Creature[];
  readonly creatureById: ReadonlyMap<string, Creature>;
  readonly creaturesByCategorie: ReadonlyMap<CreatureCategorie, readonly Creature[]>;
  readonly creatureModificateurs: readonly CreatureModificateur[];
}

let cached: BDDIndexes | null = null;

/**
 * Charge la BDD et retourne tous les indexes précalculés.
 * Mémoïsé : un seul fetch + une seule construction par session.
 */
export async function loadIndexes(): Promise<BDDIndexes> {
  if (cached) return cached;
  const bdd = await loadBDD();
  cached = buildIndexes(bdd);
  return cached;
}

/**
 * Construit les indexes depuis une BDD déjà chargée. Fonction pure,
 * réutilisable dans les tests sans réseau.
 */
export function buildIndexes(bdd: BDD): BDDIndexes {
  // Profils
  const profilById = new Map<string, Profil>();
  for (const p of bdd.profils.profils) profilById.set(p.id, p);

  // Voies de classe : la Voie du Mage est autonome (elle n'apparaît dans
  // les voies_classe_ids d'aucun profil). On la sort dans un champ dédié et on
  // n'enregistre que les voies avec `profil_id` dans voiesClasseByProfil.
  const voieClasseById = new Map<string, VoieClasse>();
  const voiesClasseByProfil = new Map<string, VoieClasse[]>();
  let voieDuMage: VoieClasse | null = null;
  for (const v of bdd.voiesClasse.voies_classe) {
    voieClasseById.set(v.id, v);
    if (v.id === 'voie-du-mage') {
      voieDuMage = v;
      continue;
    }
    const targets = v.profils_ids ?? (v.profil_id ? [v.profil_id] : []);
    for (const pid of targets) {
      let arr = voiesClasseByProfil.get(pid);
      if (!arr) {
        arr = [];
        voiesClasseByProfil.set(pid, arr);
      }
      arr.push(v);
    }
  }

  // Voies de prestige
  const voiePrestigeById = new Map<string, VoiePrestige>();
  const voiesPrestigeByCategorie = new Map<string, VoiePrestige[]>();
  for (const v of bdd.voiesPrestige.voies_prestige) {
    voiePrestigeById.set(v.id, v);
    let arr = voiesPrestigeByCategorie.get(v.categorie);
    if (!arr) {
      arr = [];
      voiesPrestigeByCategorie.set(v.categorie, arr);
    }
    arr.push(v);
  }

  // Peuples
  const peupleById = new Map<string, Peuple>();
  for (const p of bdd.peuples.peuples) peupleById.set(p.id, p);
  const voiePeupleById = new Map<string, VoiePeuple>();
  for (const v of bdd.peuples.voies_peuples) voiePeupleById.set(v.id, v);

  // Créatures (agrégat + par catégorie + par id)
  const { humanoides, animaux, fantastiques, modificateurs } = bdd.creatures;
  const creatureModificateurs: readonly CreatureModificateur[] = modificateurs ?? [];
  const annotate = (arr: readonly Creature[], categorie: CreatureCategorie) =>
    arr.map(
      (c) => ({ ...c, _categorie: categorie }) as Creature & { _categorie: typeof categorie },
    );
  const creaturesAll: readonly Creature[] = [
    ...annotate(humanoides, 'humanoides'),
    ...annotate(animaux, 'animaux'),
    ...annotate(fantastiques, 'fantastiques'),
  ];
  const creatureById = new Map<string, Creature>();
  for (const c of creaturesAll) creatureById.set(c.id, c);
  const creaturesByCategorie = new Map<CreatureCategorie, readonly Creature[]>([
    ['humanoides', humanoides],
    ['animaux', animaux],
    ['fantastiques', fantastiques],
  ]);

  return {
    bdd,
    profilById,
    voieClasseById,
    voiesClasseByProfil,
    voieDuMage,
    voiePrestigeById,
    voiesPrestigeByCategorie,
    peupleById,
    voiePeupleById,
    creaturesAll,
    creatureById,
    creaturesByCategorie,
    creatureModificateurs,
  };
}

/**
 * Trie une liste de créatures par NC croissant (stable, respecte l'ordre
 * alphabétique en cas d'égalité).
 */
export function sortCreaturesByNc(list: readonly Creature[]): Creature[] {
  const out = [...list];
  out.sort((a, b) => {
    const na = typeof a.nc === 'number' ? a.nc : parseNc(String(a.nc ?? ''));
    const nb = typeof b.nc === 'number' ? b.nc : parseNc(String(b.nc ?? ''));
    if (na !== nb) return na - nb;
    return a.nom.localeCompare(b.nom, 'fr');
  });
  return out;
}

/**
 * Vide le cache d'indexes (utile pour les tests).
 */
export function _resetIndexesCache(): void {
  cached = null;
}
